import * as dayjs from 'dayjs';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { ITicketSyncEvent, SyncEvent, SyncEventSource } from '../sync-event.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { ThirdPartyEventTicket } from './third-party-event-ticket.js';

export class ThirdPartyTicketScannedEvent extends SyncEvent implements ITicketSyncEvent {
  static readonly TYPE_ID: string = '7b2f6e21854a4440a6426ea3ed4de97d';

  constructor(
      public readonly uniqueId: string,
      public readonly creatorId: string | null,
      public readonly deviceId: string | null,
      public readonly creationDate: dayjs.Dayjs,
      public readonly source: SyncEventSource,
      public readonly ticketId: string,
      public readonly ticket: ThirdPartyEventTicket,
  ) {
    super();
  }

  get $typeId(): string {
    return ThirdPartyTicketScannedEvent.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): ThirdPartyTicketScannedEvent {
    return new ThirdPartyTicketScannedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        MapUtils.get<string>(map, 'ticketId'),
        context.fromData<ThirdPartyEventTicket>(MapUtils.get<SerializedData>(map, 'ticket')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'ticketId': this.ticketId,
      'ticket': context.toData(this.ticket),
    };
  }

  clone(): this {
    return new ThirdPartyTicketScannedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.ticketId,
        this.ticket.clone(),
    ) as this;
  }

  isSame(other: ThirdPartyTicketScannedEvent): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        other.ticketId === this.ticketId &&
        isSameData(other.ticket, this.ticket);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof ThirdPartyTicketScannedEvent;
  }

  static assertType(order: DataModel): order is ThirdPartyTicketScannedEvent {
    return order instanceof ThirdPartyTicketScannedEvent;
  }
}

export class ThirdPartyTicketScannedEventBuilder extends DataModelBuilder<ThirdPartyTicketScannedEvent> {
  build(context: SerializationContext, map: SerializedData): ThirdPartyTicketScannedEvent {
    return ThirdPartyTicketScannedEvent.fromMap(context, map);
  }
}
